import {
  DraftSession,
  ILayout,
  IRegistrationConsent,
  ITool,
  MediaWithUrl,
  RadioLineParams,
  RegistrationConsentItem,
  RegistrationConsentReference,
  TemplateRouting,
  TraitDisplayTypes,
  TraitWrapper,
} from 'types'

export function getConsentByRegion(
  consentRefs: RegistrationConsentReference[] | undefined,
  consents: IRegistrationConsent[] | undefined
): Partial<Record<'default' | 'row', IRegistrationConsent>> {
  if (!consentRefs?.length || !consents?.length) return {}
  const byId = Object.fromEntries(consents.map(c => [c.id, c]))
  const entries: Array<['default' | 'row', IRegistrationConsent]> = []
  for (const ref of consentRefs) {
    const consent = byId[ref.consentId]
    if (consent) entries.push([ref.region, consent])
  }
  return Object.fromEntries(entries) as Partial<
    Record<'default' | 'row', IRegistrationConsent>
  >
}

export function getDisclaimerFromConsent(
  consents: IRegistrationConsent[] | undefined,
  consentByRegion: Partial<Record<'default' | 'row', IRegistrationConsent>>,
  resideInDefault: string
) {
  if (!consents?.length) return undefined
  if (consents.length === 1) return consents[0].before ?? undefined
  const consent = consentByRegion[resideInDefault === 'yes' ? 'default' : 'row']
  const before = consent?.before
  if (before) return before
}

export function getDuringRegistrationConsentInfo(
  consents: IRegistrationConsent[] | undefined,
  consentByRegion: Partial<Record<'default' | 'row', IRegistrationConsent>>,
  resideInDefault: string
) {
  if (!consents?.length) return undefined
  if (consents.length === 1) return consents[0].during?.text ?? undefined
  const consent = consentByRegion[resideInDefault === 'yes' ? 'default' : 'row']
  const duringText = consent?.during?.text
  if (duringText) return duringText
}

export function getOptInFromConsent(
  consents: IRegistrationConsent[] | undefined,
  consentByRegion: Partial<Record<'default' | 'row', IRegistrationConsent>>,
  resideInDefault: string
) {
  if (!consents?.length) return undefined
  if (consents.length === 1)
    return consents[0].after?.text?.en ?? consents[0].after?.items
  const consent = consentByRegion[resideInDefault === 'yes' ? 'default' : 'row']
  const consentAfter = consent?.after?.text?.en || consent?.after?.items
  return consentAfter ?? undefined
}

export function getOptInItems(
  optInFromConsent: unknown
): RegistrationConsentItem[] | undefined {
  const isItems = Array.isArray(optInFromConsent) && optInFromConsent.length > 0
  return isItems ? (optInFromConsent as RegistrationConsentItem[]) : undefined
}

export interface ConsentDerivedValues {
  consentByRegion: Partial<Record<'default' | 'row', IRegistrationConsent>>
  disclaimerFromConsent: ReturnType<typeof getDisclaimerFromConsent>
  duringRegistrationConsentInfo: ReturnType<typeof getDuringRegistrationConsentInfo>
  optInFromConsent: ReturnType<typeof getOptInFromConsent>
  optInItems: RegistrationConsentItem[] | undefined
}

export function getConsentDerivedValues(
  consentRefs: RegistrationConsentReference[] | undefined,
  consents: IRegistrationConsent[] | undefined,
  resideInDefault: string
): ConsentDerivedValues {
  const consentByRegion = getConsentByRegion(consentRefs, consents)
  const optInFromConsent = getOptInFromConsent(consents, consentByRegion, resideInDefault)
  return {
    consentByRegion,
    disclaimerFromConsent: getDisclaimerFromConsent(consents, consentByRegion, resideInDefault),
    duringRegistrationConsentInfo: getDuringRegistrationConsentInfo(
      consents,
      consentByRegion,
      resideInDefault
    ),
    optInFromConsent,
    optInItems: getOptInItems(optInFromConsent),
  }
}

export function getRouteBasedAdditionalQues(
  additionalFields?: { [key: number]: TraitWrapper },
  routing?: TemplateRouting,
  userSession?: DraftSession
) {
  let questions: AdditionalQuestionsProps[] = []
  if (!additionalFields || !routing || !userSession) return questions

  const traitValues = userSession.traits || []
  let remainingRoute = { ...routing }

  while (true) {
    if (remainingRoute.type === 'layout') break

    const field = additionalFields[remainingRoute.key]

    const question = getQuesProps(field)
    questions.push(question)

    if (remainingRoute.valueRoutes?.length) {
      const value = traitValues.find(t => t.groupTitle === field.title?.en)
      const selectedValueTraitId = questions
        .find(q => q.prop === field.title?.en)
        ?.values.find(v => v.label === value?.title)?.id
      if (!selectedValueTraitId) break
      const valueRoute =
        value && remainingRoute.valueRoutes.find(vr => vr.fromIds.includes(selectedValueTraitId))
      if (!valueRoute) break

      remainingRoute = valueRoute.to
    } else if (remainingRoute.default) remainingRoute = remainingRoute.default
    else break
  }
  return questions
}

export interface ResultLayoutAsset {
  id: string
  title: string
  type: 'media' | 'tool'
  url: string
}

export function getResultLayoutAssets({
  additionalFields,
  layoutMap,
  media,
  routing,
  tools,
  userSession,
}: {
  additionalFields?: { [key: number]: TraitWrapper }
  layoutMap?: { [key: number]: ILayout }
  media?: MediaWithUrl[]
  routing?: TemplateRouting
  tools?: ITool[]
  userSession?: DraftSession
}): ResultLayoutAsset[] {
  const resultRoute = getResultRoute(routing, userSession, layoutMap, additionalFields)
  if (resultRoute?.type !== 'layout') return []

  const layouts = getNestedLayouts(resultRoute.key, layoutMap)
  const mediaAssets = layouts
    .filter((layout): layout is ILayout & { type: 'media' } => layout.type === 'media')
    .map(layout => media?.find(asset => asset.id === layout.mediaId))
    .filter((asset): asset is MediaWithUrl => Boolean(asset))
    .map(asset => ({
      id: asset.id,
      title: asset.title,
      type: 'media' as const,
      url: asset.url,
    }))
  const toolAssets = layouts
    .filter((layout): layout is ILayout & { type: 'tool' } => layout.type === 'tool')
    .map(layout => tools?.find(tool => tool.id === layout.toolId))
    .filter((tool): tool is ITool => Boolean(tool))
    .map(tool => ({
      id: tool.id,
      title: tool.title,
      type: 'tool' as const,
      url: tool.url,
    }))
  const assets = [...toolAssets, ...mediaAssets]

  return assets.filter(
    (asset, index) => assets.findIndex(a => a.id === asset.id && a.type === asset.type) === index
  )
}

export function getResultRoute(
  routing?: TemplateRouting,
  userSession?: DraftSession,
  layoutMap?: { [key: number]: ILayout },
  additionalFields?: { [key: number]: TraitWrapper }
) {
  if (!routing || !userSession) return routing

  let remainingRoute = { ...routing }

  while (remainingRoute.type !== 'layout') {
    const selectedValueIds = getSelectedValueIds(remainingRoute.key, userSession, additionalFields)
    const valueRoute = remainingRoute.valueRoutes?.find(route =>
      route.fromIds.some(
        id =>
          selectedValueIds.includes(id) || userSession.traits?.some(trait => trait.traitId === id)
      )
    )
    const nextRoute = valueRoute?.to || remainingRoute.default
    if (!nextRoute) break

    remainingRoute = nextRoute
  }

  if (remainingRoute.type === 'layout' && layoutMap?.[remainingRoute.key]) return remainingRoute
  return remainingRoute
}

function getSelectedValueIds(
  key: number,
  userSession?: DraftSession,
  additionalFields?: { [key: number]: TraitWrapper }
) {
  const field = additionalFields?.[key]
  if (!field) return []

  const selectedValues = userSession?.traits?.filter(trait => trait.groupTitle === field.title?.en)
  if (!selectedValues?.length) return []

  return getTraitValues(field.displayType || 'grid', field)
    .filter(value => selectedValues.some(selectedValue => selectedValue.title === value.label))
    .map(value => value.id)
}

function getNestedLayouts(key: number, layoutMap?: { [key: number]: ILayout }) {
  const layouts: ILayout[] = []
  const visitedKeys = new Set<number>()

  function addLayout(layoutKey: number) {
    if (visitedKeys.has(layoutKey)) return
    visitedKeys.add(layoutKey)

    const layout = layoutMap?.[layoutKey]
    if (!layout) return

    layouts.push(layout)
    if ('layouts' in layout) {
      layout.layouts.forEach(item => {
        if (item.key != null) addLayout(item.key)
      })
    }
    if (layout.action?.layoutKey != null) addLayout(layout.action.layoutKey)
  }

  addLayout(key)
  return layouts
}

export interface AdditionalQuestionsProps {
  title: string
  prop: string
  isMulti: boolean
  displayType?: TraitDisplayTypes
  leadField?: string
  values: TraitValueProps[]
  radioLineParams?: RadioLineParams
}

function getQuesProps(trait: TraitWrapper): AdditionalQuestionsProps {
  return {
    title: trait.titleDisplay?.en || trait.title?.en!,
    prop: trait.title?.en!,
    isMulti: trait.isMulti || false,
    displayType: trait.displayType || 'grid',
    leadField: trait.leadField,
    values: getTraitValues(trait.displayType || 'grid', trait),
    radioLineParams: trait.displayType === 'radio-line' ? trait.radioLineParams : undefined,
  }
}

export interface TraitValueProps {
  label: string
  id: string
}

function getTraitValues(displayType: TraitDisplayTypes, trait: TraitWrapper): TraitValueProps[] {
  switch (displayType) {
    case 'select':
    case 'grid':
    case 'list':
      return (trait.values ?? []).map(value => {
        const label = value.title?.en!
        return { label, id: value.id }
      })

    case 'radio-line':
      if (!trait.radioLineParams) return []
      const minValue = trait.radioLineParams?.minValue
      const maxValue = trait.radioLineParams?.maxValue
      const step = trait.radioLineParams?.step || 1
      return Array.from({ length: Math.floor((maxValue - minValue) / step) + 1 }, (_, i) => ({
        label: (minValue + i * step).toString(),
        id: (minValue + i * step).toString(),
      }))
  }
}
